import type {Organizer} from '@/apiTypes/event';
import type {Profile} from '@/apiTypes/profile';
import {useAuth} from '@/components/AuthProvider';
import {cn} from '@/lib/utils';
import {getUserOrganizers} from '@/server/organizer';
import {
  Link,
  getRouteApi,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import {
  Building2,
  ChevronDown,
  FileText,
  LayoutDashboard,
  LogOut,
  Plus,
  Upload,
  User,
} from 'lucide-react';
import {HamburgerButton} from './hamburgerButton';
import {AnimatePresence, motion} from 'motion/react';
import {type FC, type ReactNode, useEffect, useState} from 'react';
import {Avatar} from '../avatar';
import {Button} from '../button';
import {Menu, MenuItem, MenuPopup, MenuSeparator, MenuTrigger} from '../menu';
import {Logo} from '../logo';
import {DEFAULT_CUBIC, DEFAULT_DURATION} from '@/styles/animation';

const containerVariants = {
  closed: {
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
    margin: 0,
    padding: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    transition: {
      duration: DEFAULT_DURATION,
      ease: DEFAULT_CUBIC,
      delay: DEFAULT_DURATION * 0.3,
    },
  },
  open: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
    margin: -12,
    padding: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.90)',
    transition: {
      duration: DEFAULT_DURATION,
      ease: DEFAULT_CUBIC,
      delay: 0,
    },
  },
};

const mobileMenuVariants = {
  closed: {
    height: 0,
    transition: {
      duration: DEFAULT_DURATION * 1.5,
      ease: DEFAULT_CUBIC,
    },
  },
  open: {
    height: 'auto',
    opacity: 1,
    transition: {
      duration: DEFAULT_DURATION * 1.5,
      ease: DEFAULT_CUBIC,
      delay: DEFAULT_DURATION * 0.3,
    },
  },
};

interface NavbarProps {
  className?: string;
  children?: ReactNode;
}

/**
 * Top navigation bar with logo, optional centered children (desktop only),
 * auth state display, and a mobile hamburger menu.
 */
export function Navbar({className, children}: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {profile, signOut} = useAuth();
  const {featureFlags} = getRouteApi('__root__').useRouteContext();
  const blogEnabled = featureFlags['enable-blog'];
  const [userOrganizers, setUserOrganizers] = useState<Organizer[]>([]);

  useEffect(() => {
    if (!profile) {
      setUserOrganizers([]);
      return;
    }

    let cancelled = false;
    getUserOrganizers()
      .then((memberships) => {
        if (!cancelled) {
          setUserOrganizers(memberships.map((m) => m.organizer));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUserOrganizers([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [profile]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header
      className={cn(
        'w-full sticky top-0 z-50 p-3 h-[88px] overflow-visible',
        className
      )}
    >
      <motion.div
        className="mx-auto backdrop-blur-sm shadow-md overflow-hidden relative z-50"
        initial={false}
        variants={containerVariants}
        animate={isMenuOpen ? 'open' : 'closed'}
      >
        <div className="px-6 flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center shrink-0">
            <Link to="/" className="flex items-center">
              <Logo variant="dark" />
            </Link>
          </div>

          {/* Center content — desktop only */}
          {children && (
            <div className="hidden lg:flex flex-1 justify-center px-8">
              {children}
            </div>
          )}

          {/* Actions + Auth + Mobile Menu */}
          <div className="flex items-center gap-3">
            {blogEnabled && (
              <Button
                size="sm"
                variant="ghost"
                className="hidden md:inline-flex rounded-full px-4 text-neutral-900"
                asChild
              >
                <Link to="/blog">Stories</Link>
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="hidden md:inline-flex rounded-full text-neutral-900 px-4"
              asChild
            >
              <Link to="/create-event">
                <Plus className="w-4 h-4" />
                Create Event
              </Link>
            </Button>
            <Button
              size="sm"
              className="hidden md:inline-flex bg-primary hover:bg-primary/90 text-white rounded-full px-4"
              asChild
            >
              <Link to="/search">Find Events</Link>
            </Button>

            {/* Mobile Menu Button */}
            <HamburgerButton
              isOpen={isMenuOpen}
              onClick={toggleMenu}
              className="md:hidden p-2 text-neutral-900 hover:text-neutral-600 transition-colors"
            />

            {/* Auth — avatar dropdown (desktop only) */}
            <div className="hidden md:block">
              <AuthSection organizers={userOrganizers} />
            </div>
          </div>
        </div>
        {/* Mobile Menu — absolute so it doesn't affect container/page height */}
        <MobileMenu
          isOpen={isMenuOpen}
          profile={profile}
          organizers={userOrganizers}
          onSignOut={signOut}
          blogEnabled={blogEnabled}
        />
      </motion.div>

      {/* Backdrop overlay — darkens page content when mobile menu is open */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: DEFAULT_DURATION, ease: DEFAULT_CUBIC}}
            className="fixed inset-0 bg-black/20 md:hidden -z-10"
            onClick={toggleMenu}
          />
        )}
      </AnimatePresence>
    </header>
  );
}

/**
 * Auth section shown in the top bar at all screen sizes.
 * Shows avatar dropdown when logged in, login button on desktop when logged out.
 */
function AuthSection({organizers}: {organizers: Organizer[]}) {
  const {profile, signOut} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!profile) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="hidden md:inline-flex text-neutral-900"
        asChild
      >
        <Link
          to="/login"
          search={{
            redirect: `${location.pathname}${location.searchStr}${location.hash}`,
          }}
        >
          Log In
        </Link>
      </Button>
    );
  }

  return (
    <Menu>
      <MenuTrigger className="flex items-center gap-1.5 cursor-pointer rounded-full p-0.5 transition-colors hover:bg-slate-100">
        <Avatar profile={profile} />
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 hidden md:block" />
      </MenuTrigger>
      <MenuPopup align="end" sideOffset={8} className="shadow-md">
        <MenuItem
          onClick={() =>
            navigate({
              to: '/rider/$profileId',
              params: {profileId: profile.id},
            })
          }
        >
          <User className="w-4 h-4" />
          Profile
        </MenuItem>
        <MenuItem onClick={() => navigate({to: '/dashboard'})}>
          <LayoutDashboard className="w-4 h-4" />
          Dashboard
        </MenuItem>
        <MenuSeparator />
        {organizers.map((org) => (
          <MenuItem
            key={org.id}
            onClick={() =>
              navigate({
                to: '/admin/dashboard/organizer/$organizerId',
                params: {organizerId: org.id},
              })
            }
          >
            <Building2 className="w-4 h-4" />
            {org.name}
          </MenuItem>
        ))}
        {profile.level === 'superadmin' && (
          <>
            <MenuSeparator />
            <MenuItem onClick={() => navigate({to: '/admin/import'})}>
              <Upload className="w-4 h-4" />
              Import Rides
            </MenuItem>
            <MenuItem onClick={() => navigate({to: '/admin/blog'})}>
              <FileText className="w-4 h-4" />
              Blog
            </MenuItem>
          </>
        )}
        <MenuItem onClick={() => navigate({to: '/create-organizer'})}>
          <Plus className="w-4 h-4" />
          Create Organization
        </MenuItem>
        <MenuSeparator />
        <MenuItem onClick={() => signOut()}>
          <LogOut className="w-4 h-4" />
          Sign Out
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}

interface MobileMenuProps {
  isOpen: boolean;
  profile: Profile | null;
  organizers: Organizer[];
  onSignOut: () => void;
  blogEnabled: boolean;
}

/**
 * Mobile navigation menu that expands within the navbar container.
 * Uses motion height animation for a smooth reveal.
 */
const MobileMenu: FC<MobileMenuProps> = ({
  isOpen,
  profile,
  organizers,
  onSignOut,
  blogEnabled,
}) => {
  const location = useLocation();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.nav
          variants={mobileMenuVariants}
          initial="closed"
          animate="open"
          exit="closed"
          className="md:hidden overflow-hidden"
        >
          <div className="px-6 pb-6 pt-2 flex flex-col gap-3">
            <Button
              size="sm"
              className="justify-center w-full bg-primary hover:bg-primary/90 rounded-full"
              asChild
            >
              <Link to="/search">Find Events</Link>
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="justify-center w-full rounded-full text-neutral-900"
              asChild
            >
              <Link to="/create-event">
                <Plus className="w-4 h-4" />
                Create Event
              </Link>
            </Button>
            {blogEnabled && (
              <Button
                size="sm"
                variant="ghost"
                className="justify-center w-full rounded-full text-neutral-900"
                asChild
              >
                <Link to="/blog">Stories</Link>
              </Button>
            )}

            {!profile && (
              <>
                <hr className="border-neutral-200" />
                <Button
                  size="sm"
                  className="justify-center w-full bg-neutral-900 hover:bg-neutral-800 text-white rounded-full"
                  asChild
                >
                  <Link
                    to="/login"
                    search={{
                      redirect: `${location.pathname}${location.searchStr}${location.hash}`,
                    }}
                  >
                    Log In
                  </Link>
                </Button>
              </>
            )}

            {profile && (
              <>
                <hr className="my-2 border-neutral-200" />
                <Link
                  to="/rider/$profileId"
                  params={{profileId: profile.id}}
                  className="py-2 text-center text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                >
                  Profile
                </Link>
                <Link
                  to="/dashboard"
                  className="py-2 text-center text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                >
                  Dashboard
                </Link>
                <hr className="my-2 border-neutral-200" />
                {organizers.map((org) => (
                  <Link
                    key={org.id}
                    to="/admin/dashboard/organizer/$organizerId"
                    params={{organizerId: org.id}}
                    className="py-2 text-center text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                  >
                    {org.name}
                  </Link>
                ))}
                {profile.level === 'superadmin' && (
                  <>
                    <Link
                      to="/admin/import"
                      className="py-2 text-center text-sm text-neutral-600 hover:text-neutral-900 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Upload className="w-4 h-4" />
                      Import Rides
                    </Link>
                    <Link
                      to="/admin/blog"
                      className="py-2 text-center text-sm text-neutral-600 hover:text-neutral-900 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <FileText className="w-4 h-4" />
                      Blog
                    </Link>
                  </>
                )}
                <Link
                  to="/create-organizer"
                  className="py-2 text-center text-sm text-neutral-600 hover:text-neutral-900 transition-colors flex items-center justify-center gap-1.5"
                >
                  <Plus className="w-4 h-4" />
                  Create Organization
                </Link>
                <hr className="my-2 border-neutral-200" />
                <button
                  type="button"
                  onClick={onSignOut}
                  className="py-2 text-center text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                >
                  Sign Out
                </button>
              </>
            )}
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
};
